<template>
  <Modal
    :title="computedTitle"
    :modal="modalUsuario"
    max-width="960px"
    cardTextClass="pa-0"
    @close="$emit('close')"
    @save="save"
  >
    <div slot="header-right">
      <v-btn fab small text @click="$emit('close')" class="mr-3">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-btn fab small color="primary" @click="save" :loading="loading">
        <v-icon>mdi-check</v-icon>
      </v-btn>
    </div>
    <v-row slot="inner-body" no-gutters>
      <v-col cols="12" class="pa-0">
        <Tabs
          background-color="grey lighten-3"
          :tabsHeader="usuarioTabs"
          :tab="tab"
          headerKey="descricao"
          @tabChanged="tab = $event"
        >
          <v-form
            slot="Informações"
            @submit.prevent="save"
            v-model="valid"
            ref="usuarioForm"
            lazy-validation
          >
            <v-col cols="12" class="px-4">
              <Select
                @change="usuario.tipo = $event"
                :value="usuario.tipo"
                :items="['admin', 'usuario', 'cliente']"
                label="Tipo"
                name="tipo-usuario"
                :rules="[rules.required]"
                filled
                required
              />
              <Input
                @input="usuario.nome = $event"
                :value="usuario.nome"
                label="Nome"
                name="nome"
                :rules="[rules.required]"
                :autofocus="modalUsuario"
                filled
                required
              />
              <Input
                @input="usuario.cpf_cnpj = $event"
                :value="usuario.cpf_cnpj"
                label="CPF/CNPJ"
                name="cpf_cnpj"
                :rules="[rules.required]"
                class="input-number"
                filled
                required
              />
              <Input
                @input="usuario.email = $event"
                :value="usuario.email"
                type="email"
                label="E-mail"
                name="email"
                :rules="[rules.required, rules.email]"
                filled
                required
              />
              <Input
                v-if="!usuario._id || isAdmin"
                @input="usuario.senha = $event"
                :value="usuario.senha"
                :type="showPassword ? 'text' : 'password'"
                label="Senha"
                name="senha"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @clickappend="showPassword = !showPassword"
                filled
              />
              <Input
                v-if="!usuario._id || isAdmin"
                @input="confirmeSenha = $event"
                :value="confirmeSenha"
                :type="showPassword ? 'text' : 'password'"
                label="Confirme a Senha"
                name="confirme_senha"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @clickappend="showPassword = !showPassword"
                :rules="[customRules.confirmeSenha]"
                filled
              />
            </v-col>
          </v-form>
          <v-col slot="Tags">
            <v-card flat>
              <v-card-title class="pa-0 justify-space-between">
                <span class="body-1 font-weight-bold">Tags ({{ usuario.tags.length }})</span>
                <v-col cols="4">
                  <Autocomplete
                    :items="tags"
                    label="Selecione"
                    name="tags"
                    item-text="descricao"
                    return-object
                    @change="changeTag"
                    filled
                    dense
                    hide-details
                    :resetAfterChange="true"
                  />
                </v-col>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-card v-if="usuario.tags.length">
                  <ListItem
                    v-for="tag of usuario.tags"
                    :key="tag._id"
                    :title="tag.descricao"
                    dense
                    @icon-clicked="removeTag(tag)"
                  />
                </v-card>
                <Alert v-else text="Nenhuma tag adicionada nesse usuário" />
              </v-card-text>
            </v-card>
          </v-col>
        </Tabs>
      </v-col>
    </v-row>
  </Modal>
</template>

<script>
import Usuario from '@/domain/Usuario';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ModalUsuario',
  props: {
    modalUsuario: {
      type: Boolean,
      default: false
    },
    editUsuario: {
      default: null
    }
  },
  components: {
    Modal: () => import('../shared/Modal'),
    Input: () => import('@/components/shared/Input'),
    Select: () => import('@/components/shared/Select'),
    Autocomplete: () => import('@/components/shared/Autocomplete'),
    Tabs: () => import('@/components/shared/Tabs'),
    ListItem: () => import('@/components/shared/ListItem'),
    Alert: () => import('@/components/shared/Alert'),
  },
  data() {
    return {
      tab: '',
      valid: true,
      usuario: new Usuario(),
      confirmeSenha: '',
      showPassword: false,
      usuarioTabs: [
        { descricao: 'Informações' },
        { descricao: 'Tags' },
      ],
      customRules: {
        confirmeSenha: value => {
          if (!this.usuario.senha && !value) return true;

          return !!value && value === this.usuario.senha || 'Senhas não conferem!';
        }
      }
    }
  },
  watch: {
    modalUsuario(value) {
      if (!value) {
        this.valid = true;
        this.tab = '';
        this.usuario = new Usuario();
        this.confirmeSenha = '';
        this.showPassword = false;
      } else if (value && !this.tags.length) {
        this.fetchTags();
      }
    },
    editUsuario(value) {
      if (value) this.usuario = JSON.parse(JSON.stringify(value));
    }
  },
  computed: {
    ...mapGetters(['loading', 'rules', 'isAdmin', 'tags']),
    computedTitle() {
      return this.editUsuario ? 'Edita Usuario' : 'Novo Usuario';
    }
  },
  methods: {
    ...mapActions(['addUsuario', 'updateUsuario', 'fetchTags']),
    changeTag(obj) {
      const addTag = { ...obj };

      const tagExists = this.usuario.tags.find(tag => tag.descricao === addTag.descricao);

      if (!tagExists) {
        this.usuario.tags.push(addTag);
      } else {
        this.$store.commit('SHOW_SNACKBAR', { text: `Tag (${addTag.descricao}) já existe no usuário`, status: 'error' });
      }
    },
    removeTag(tagSelected) {
      if (confirm(`Deseja remover a tag ${tagSelected.descricao} do usuário?`)) {
        this.usuario.tags = this.usuario.tags.filter(tag => tag.descricao !== tagSelected.descricao);
      }
    },
    async save() {
      if (this.$refs.usuarioForm.validate()) {
        if (this.editUsuario) {
          const success = await this.updateUsuario({ ...this.usuario });
          if (!success) return;
          this.$emit('close');
        } else {
          const success = await this.addUsuario({ ...this.usuario });
          if (!success) return;
          this.$emit('close');
        }
      }
    }
  }
}
</script>